import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@material-ui/core";
import { isEqual } from "lodash";
import { SagaService, IAction } from "front-end-lib";
import { eStudySaga, eLookupSaga } from "../../../../sagas";
import { eUtilActionTypes } from "../../../../store";
import { useStudySiteProtocols } from "../../../../hooks";
import {
  StudySite,
  StudySiteDTO,
  Person,
  ILookup,
  IMddStudyProps,
  IAddEditStudySite,
  IStudyProtocol,
  IAddEditStudySitePiHistory
} from "../../../../model";
import { isLoading, useSaga, store } from "../../../../utils";
import { toDateFormat } from "../../../../utils/functionsHelper";
import { BktBreadcrumb, withMddStudy, MddForm } from "../../../../components";
import {
  sortProtocols,
  sortProtocolsForEditMode,
  getBreadcrumbConfig,
  MddStudySiteProtocolCard,
  MddStudySiteProtocolDialog,
  MddStudySiteGeneralCard,
  MddStudySiteDeleteProtocolConfirmDialog,
  MddStudySiteSaveButton
} from "../shared";

const _MddEditStudySite = ({
  match: {
    params: { id, siteId }
  },
  study
}: IMddStudyProps) => {
  const [studySite, setStudySite] = useState<IAddEditStudySite>({
    studyId: id,
    studySiteId: siteId || "",
    siteId: "",
    siteName: "",
    sponsorSiteId: "",
    primaryInvestigator: undefined,
    primaryInvestigatorHistory: [],
    protocols: [],
    isActive: true
  });
  const [siteName, setSiteName] = useState("");
  const [displayProtocolDialog, setDisplayProtocolDialog] = useState(false);
  const [sponsorSiteIdHasError, setSponsorSiteIdHasError] = useState(false);
  const [
    originalStudySite,
    setOriginalStudySite
  ] = useState<IAddEditStudySite | null>(null);

  const { data: sitesData, loading: sitesLoading } = useSaga<ILookup[]>({
    startType: eLookupSaga.FETCH_SITES.toString(),
    successType: eLookupSaga.FETCH_SITES_SUCCESS.toString()
  });
  const sites = useMemo(() => sitesData ?? [], [sitesData]);

  const { data: studyProtocolsRaw = [], loading: protocolsLoading } = useSaga<
    IStudyProtocol[]
  >({
    startType: eStudySaga.FETCH_STUDY_PROTOCOLS.toString(),
    successType: eStudySaga.FETCH_STUDY_PROTOCOLS_SUCCESS.toString(),
    payload: id
  });

  const {
    data: fetchedStudySite,
    loading: studySiteLoading,
    reload: reloadStudySite
  } = useSaga<IAddEditStudySite>({
    startType: eStudySaga.FETCH_STUDY_SITE.toString(),
    successType: eStudySaga.FETCH_STUDY_SITE_SUCCESS.toString(),
    payload: siteId ? new StudySiteDTO(id, siteId, "", true) : null
  });

  const studyProtocols = useMemo(() => sortProtocols(studyProtocolsRaw || []), [
    studyProtocolsRaw
  ]);

  const resolvedSiteName = useMemo(() => {
    if (!fetchedStudySite?.siteId) {
      return "";
    }

    const siteLookup = sites.find(
      (x: ILookup) => x.value === fetchedStudySite.siteId
    );

    return siteLookup?.label || "";
  }, [fetchedStudySite?.siteId, sites]);

  // Update study site state when fetched data comes in
  useEffect(() => {
    if (fetchedStudySite) {
      // Map protocols and mark as existing, then sort alphabetically
      const existingProtocols = (fetchedStudySite.protocols || []).map(
        protocol => ({
          ...protocol,
          isExisting: true // Mark existing protocols as existing
        })
      );

      // Sort existing protocols alphabetically by name
      const sortedProtocols = sortProtocolsForEditMode(existingProtocols);

      const newStudySite: IAddEditStudySite = {
        studyId: fetchedStudySite.studyId,
        studySiteId: siteId,
        siteId: fetchedStudySite.siteId,
        siteName: resolvedSiteName,
        sponsorSiteId: fetchedStudySite.sponsorSiteId,
        isActive: fetchedStudySite.isActive,
        primaryInvestigator: fetchedStudySite.primaryInvestigator,
        primaryInvestigatorHistory: fetchedStudySite.primaryInvestigatorHistory,
        protocols: sortedProtocols
      };

      setStudySite(newStudySite);
      setSiteName(resolvedSiteName);

      // Store the original study site data for comparison
      setOriginalStudySite(newStudySite);
    }
  }, [fetchedStudySite, resolvedSiteName, siteId, id]);

  // Sync loading states with Redux store for MddForm loading indicator
  useEffect(() => {
    const sitesKey = `undefined_${eLookupSaga.FETCH_SITES}`;
    const protocolsKey = `undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`;
    const studySiteKey = `undefined_${eStudySaga.FETCH_STUDY_SITE}`;

    store.dispatch({
      type: eUtilActionTypes.SET_LOADING,
      payload: { key: sitesKey, loading: sitesLoading }
    });

    store.dispatch({
      type: eUtilActionTypes.SET_LOADING,
      payload: { key: protocolsKey, loading: protocolsLoading }
    });

    store.dispatch({
      type: eUtilActionTypes.SET_LOADING,
      payload: { key: studySiteKey, loading: studySiteLoading }
    });
  }, [sitesLoading, protocolsLoading, studySiteLoading]);

  // Reload study site data after successful save to display protocols correctly
  useEffect(() => {
    if (!siteId) return;

    const removeSaveStudySiteSub = SagaService.subscribeToSaga(
      eStudySaga.SAVE_STUDY_SITE,
      (action: IAction) => {
        if (action.type === eStudySaga.SAVE_STUDY_SITE_SUCCESS) {
          // Reload the study site to get updated protocol data
          // useSaga's reload will update fetchedStudySite, which triggers the useEffect above
          reloadStudySite();
        }
      }
    );

    return () => {
      removeSaveStudySiteSub();
    };
  }, [id, siteId, reloadStudySite]);

  // Protocol management hook
  const {
    protocolsAddedByUser,
    hasProtocolValidationErrors,
    deleteConfirmation,
    handleAddProtocols,
    handleProtocolDeleteRequest,
    handleProtocolDeleteConfirm,
    handleProtocolDeleteCancel,
    handleProtocolValidationError
  } = useStudySiteProtocols(studySite, setStudySite, true); // Pass isEditMode = true

  // Handle main sponsor site ID changes
  const handleStudySiteChange = useCallback(
    (updatedStudySite: IAddEditStudySite) => {
      // Just update the study site - no automatic protocol updates in edit mode
      setStudySite(updatedStudySite);
    },
    []
  );

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // Convert interface data to StudySite class instance for service call
    const protocolsForSave = (studySite.protocols || []).map(protocol => {
      const { isExisting, ...protocolWithoutFlag } = protocol;
      return protocolWithoutFlag;
    });

    const primaryInvestigatorForSave = studySite.primaryInvestigator
      ? Object.assign(new Person(), studySite.primaryInvestigator)
      : undefined;

    const studySiteForSave = Object.assign(new StudySite(), {
      StudyId: studySite.studyId,
      StudySiteId: studySite.studySiteId,
      SiteId: studySite.siteId,
      SiteName: studySite.siteName,
      SponsorSiteId: studySite.sponsorSiteId,
      PrimaryInvestigator: primaryInvestigatorForSave,
      IsActive: studySite.isActive,
      PrimaryInvestigatorHistory: studySite.primaryInvestigatorHistory,
      Protocols: protocolsForSave
    });

    SagaService.dispatchSaga({
      type: eStudySaga.SAVE_STUDY_SITE,
      payload: { newStudySite: studySiteForSave, existingStudySite: true }
    });
  };

  const breadcrumbConfig = useMemo(
    () =>
      getBreadcrumbConfig(
        study.StudyName || "",
        study.StudyId || "",
        "edit",
        siteName
      ),
    [study.StudyName, study.StudyId, siteName]
  );

  // Format PI history data for display
  const piHistoryData = useMemo(() => {
    const { primaryInvestigatorHistory } = studySite;

    if (primaryInvestigatorHistory?.length) {
      return primaryInvestigatorHistory.map(
        (item: IAddEditStudySitePiHistory, i: number) => ({
          name: item.name,
          startDate: toDateFormat(item.startDate),
          endDate: toDateFormat(item.endDate),
          key: `history-${i}`
        })
      );
    }

    return null;
  }, [studySite]);

  // Check if study site has changed from original
  const hasStudySiteChanged = useMemo(() => {
    if (!originalStudySite) return false;

    // Use lodash deep equality comparison to detect any changes.
    // This automatically catches changes to PI, isActive, protocols, etc. and removes
    // the need for some really ugly comparison logic that will have to be updated with any
    // new editable fields.  This will just work for any new fields and handles edge cases properly
    // (key ordering, undefined values, nested objects, etc.)
    return !isEqual(studySite, originalStudySite);
  }, [studySite, originalStudySite]);

  // Loading keys used for both isLoading check and MddForm
  const loadingKeys = useMemo(
    () => [
      `undefined_${eLookupSaga.FETCH_SITES}`,
      `undefined_${eStudySaga.SAVE_STUDY_SITE}`,
      `undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`,
      `undefined_${eStudySaga.FETCH_STUDY_SITE}`
    ],
    []
  );

  const disableSaveButton =
    isLoading(...loadingKeys) ||
    !studySite.protocols?.length ||
    hasProtocolValidationErrors ||
    sponsorSiteIdHasError ||
    !hasStudySiteChanged;

  return (
    <div className="mdd-form--fullheight">
      <BktBreadcrumb
        rootText={breadcrumbConfig.rootText}
        rootUrl={breadcrumbConfig.rootUrl}
        currentLocation={breadcrumbConfig.currentLocation}
        currentNestedLocation={breadcrumbConfig.currentNestedLocation}
        currentNestedLocationUrl={breadcrumbConfig.currentNestedLocationUrl}
      />

      <div className="mdd-study-site--general-info-title">General Info</div>

      <MddForm
        containerClass="mdd-study-site--form-container mdd-study-site--form-container-with-sticky-save"
        keys={loadingKeys}
      >
        <div className="mdd-form--body mdd-form--body-with-sticky-save">
          <MddStudySiteGeneralCard
            studySite={studySite}
            sites={sites || []}
            protocolsAddedByUser={protocolsAddedByUser}
            onStudySiteChange={handleStudySiteChange}
            onSponsorSiteIdValidationError={setSponsorSiteIdHasError}
            piHistoryData={piHistoryData}
          />

          <div className="mdd-study-site--protocol-section-card">
            <div className="mdd-study-site--protocol-section-header mdd-study-site--protocol-section-header--with-button">
              <div className="mdd-study-site--protocol-section-title">
                Study Protocol Participation
              </div>
              <div className="mdd-study-site--protocol-add-button-container">
                <Button
                  className="mdd-study-site--protocol-add-button"
                  size="small"
                  id="btnAddProtocol"
                  disableFocusRipple
                  disableRipple
                  variant="outlined"
                  disabled={
                    !studySite.sponsorSiteId ||
                    studyProtocols.length === 0 ||
                    !studySite.isActive
                  }
                  onClick={() => {
                    setDisplayProtocolDialog(true);
                  }}
                >
                  Add Protocol
                </Button>
              </div>
            </div>

            <div className="mdd-study-site--protocol-section-content">
              {studyProtocols.length === 0 &&
                !isLoading(`undefined_${eStudySaga.FETCH_STUDY_PROTOCOLS}`) && (
                  <div className="mdd-study-site--protocol-sponsor-note">
                    * No protocols exist for this study, you must add at least
                    one study protocol in order to edit a site
                  </div>
                )}

              {/* Protocol Participation Cards */}
              {(studySite.protocols?.length ?? 0) > 0 && (
                <div className="mdd-study-site--protocol-cards-container mdd-study-site--protocol-cards-container--edit">
                  {studySite.protocols.map((protocol, index) => {
                    const isLastNewProtocol =
                      !protocol.isExisting &&
                      index < studySite.protocols.length - 1 &&
                      studySite.protocols[index + 1]?.isExisting;

                    return (
                      <React.Fragment key={protocol.id}>
                        <MddStudySiteProtocolCard
                          protocol={protocol}
                          studySite={studySite}
                          isEditMode
                          isSiteActive={studySite.isActive}
                          onDelete={handleProtocolDeleteRequest}
                          onStudySiteChange={setStudySite}
                          onValidationError={handleProtocolValidationError}
                        />
                        {isLastNewProtocol && (
                          <hr className="mdd-study-site--protocol-divider" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sticky Save Button Section */}
        <MddStudySiteSaveButton
          onSave={handleSubmit}
          disabled={disableSaveButton}
        />
      </MddForm>

      {/* Add Protocol Dialog */}
      {displayProtocolDialog && (
        <MddStudySiteProtocolDialog
          open
          studyProtocols={studyProtocols}
          existingProtocols={studySite.protocols || []}
          onAddProtocols={handleAddProtocols}
          onClose={() => setDisplayProtocolDialog(false)}
        />
      )}

      {/* Delete Protocol Confirmation Dialog */}
      {deleteConfirmation.isOpen && (
        <MddStudySiteDeleteProtocolConfirmDialog
          open
          protocolName={deleteConfirmation.protocolName}
          onConfirm={handleProtocolDeleteConfirm}
          onClose={handleProtocolDeleteCancel}
        />
      )}
    </div>
  );
};

export const MddEditStudySite = withMddStudy()(_MddEditStudySite);
